import type * as rxjs from 'rxjs'

import type { Feature } from '../../feature'
import { Action } from '../action'
import { Filter } from '../filter'
import { Op } from '../op'
import { Producer } from '../producer'

import type { Config } from './config'

/**
 * Alias used when {@link Action} is shadowed
 */
import _Action = Action

/**
 * TODO
 */
export interface OpManager<
  TState,
  // out: this.op, this.from(...).create(...).op
  out TOp extends Op = Op,
  // in: this.from(...).create(inputData)
  in TInputData = unknown,
  // out: this.from(...).create(...).data
  out TData = TInputData,
> extends Producer.Factory<TInputData, Action<TState, TOp, TData>>,
    Filter.WithObservable<Action<TState, TOp, TData>> {
  readonly op: TOp
}
export namespace OpManager {
  export type Constraint<
    TState = any,
    TOp extends Op = any,
    TInputData = any,
    TData = any,
  > = OpManager<TState, TOp, TInputData, TData>

  export type FromConfig<TOpName extends string, TConfig> =
    TConfig extends Config.StandaloneOp<
      infer TState,
      infer TInputData,
      infer TData
    >
      ? OpManager<TState, Op<TOpName, undefined>, TInputData, TData>
      : never

  export function create<TState, TOp extends Op, TInputData, TData>(
    feature: Feature.Constraint<TState>,
    op: TOp,
    config: Pick<
      Config.StandaloneOp<TState, TInputData, TData>,
      'data' | 'reducer' | 'reducerDeprecated'
    >,
  ): OpManager<TState, TOp, TInputData, TData>
  export function create(
    feature: Feature.Constraint,
    op: Op,
    {
      data: transformInputData,
      reducer,
      reducerDeprecated,
    }: Pick<
      Config.StandaloneOp<any, unknown, unknown>,
      'data' | 'reducer' | 'reducerDeprecated'
    >,
  ): OpManager<any, Op, unknown> {
    return {
      ...Filter.where(
        action => action.featureId === feature.id && Op.isEqual(action.op, op),
      ),
      from(source, { traceStash } = {}) {
        return Producer.from(
          feature.injectables,
          data =>
            new Action({
              data: transformInputData(data),
              feature,
              op,
              reducer,
              reducerDeprecated,
              source,
              traceStash,
            }),
        )
      },
      observe() {
        return feature.actions$.pipe(this.applyFilter())
      },
      op,
    }
  }

  /**
   * TODO
   */
  export type RequestGroup<
    TState,
    TOpName extends string,
    TRequestInput,
    TRequest,
    TResponseInput,
    TResponse,
  > = {
    [TOpStatus in Op.Status]: OpManager<
      TState,
      Op<TOpName, TOpStatus>,
      Op.Status.Data.Input<
        TOpStatus,
        TRequestInput,
        TRequest,
        TResponseInput,
        TResponse
      >,
      Op.Status.Data<TOpStatus, TRequest, TResponse>
    >
  } & Producer.WithExecute.Factory<
    TRequestInput,
    // When creating/dispatching, this just creates a request action
    Action<
      TState,
      Op<TOpName, 'request'>,
      Op.Status.Data<'request', TRequest, TResponse>
    >,
    TResponse
  > & {
      relay: (
        mapper: (
          // NOTE: we need to `Pick` only the properties that correspond to an
          // `Op.Status` as the other properties cause unfortunate type
          // extension issues
          forwardTo: <TTo extends RequestGroup.relay.Target.Constraint>(
            to: TTo,
            mapping: RequestGroup.relay.Mapping<TTo, TResponseInput>,
          ) => RequestGroup.relay.Instruction.Forward<TTo, TResponseInput>,
          request: Op.Status.Data.NoMissing<'request', TRequest, TResponse>,
          source: Action<
            TState,
            Op<TOpName, 'request'>,
            Op.Status.Data<'request', TRequest, TResponse>
          >,
        ) => rxjs.Observable<
          RequestGroup.relay.Instruction<
            RequestGroup.relay.Target.Constraint,
            TResponseInput
          >
        >,
        options?: RequestGroup.relay.Options,
      ) => {
        /**
         * Makes the relay live by dispatching each {@link Action} that arises
         *
         * Usually, it is preffered to just return {@link asObservable} in an effect
         */
        init: () => rxjs.Subscription
        /**
         * Generally used in an effect. Emits stream of {@link Action actions} to be dispatched
         *
         * @example
         * TODO:
         */
        asObservable: rxjs.Observable<Action>
      }
    } & RequestGroup.Marker<
      TState,
      TOpName,
      TRequestInput,
      TRequest,
      TResponseInput,
      TResponse
    >
  export namespace RequestGroup {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export interface Constraint<
      TState = any,
      TOpName extends string = any,
      TRequestInput = any,
      TRequest = any,
      TResponseInput = any,
      TResponse = any,
    > extends RequestGroup<
        TState,
        TOpName,
        TRequestInput,
        TRequest,
        TResponseInput,
        TResponse
      > {}

    /**
     * {@link RequestGroup} is a complicated type, and trying to use
     * {@link RequestGroup.Constraint} as the constraint for a type parameter
     * has a lot of complicated edge cases that causes problems, particularly in
     * {@link RequestGroup.relay}. Thus, this type can be used to have a simpler
     * type parameter for {@link RequestGroup} that suffers from fewer edge
     * cases
     */
    export type Marker<
      TState = any,
      TOpName extends string = any,
      TRequestInput = any,
      TRequest = any,
      TResponseInput = any,
      TResponse = any,
    > = Partial<
      Record<
        typeof Marker.SYMBOL,
        {
          state: TState
          opName: TOpName
          requestInput: TRequestInput
          request: TRequest
          responseInput: TResponseInput
          response: TResponse
        }
      >
    >
    export namespace Marker {
      /**
       * __NOTE:__ this is not actually used at runtime. It is just used to
       * create a `unique symbol` type at compile-time.
       */
      export const SYMBOL = Symbol()

      export type Payload<TMarker extends Marker> = NonNullable<
        TMarker[typeof SYMBOL]
      >

      export type Data<
        TMarker extends Marker,
        TOpStatus extends Op.Status,
      > = Op.Status.Data<
        TOpStatus,
        Payload<TMarker>['request'],
        Payload<TMarker>['response']
      >

      export type Input<
        TMarker extends Marker,
        TOpStatus extends Op.Status,
      > = Op.Status.Data.Input<
        TOpStatus,
        Payload<TMarker>['requestInput'],
        Payload<TMarker>['request'],
        Payload<TMarker>['responseInput'],
        Payload<TMarker>['response']
      >

      export type Action<
        TMarker extends Marker,
        TOpStatus extends Op.Status,
      > = _Action<
        Payload<TMarker>['state'],
        Op<Payload<TMarker>['opName'], TOpStatus>,
        Marker.Data<NoInfer<TMarker>, TOpStatus>
      >
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type FromConfig<TOpName extends string, TConfig> =
      TConfig extends Config.RequestOp<
        infer TState,
        infer TRequestInput,
        infer TRequest,
        infer TResponseInput,
        infer TResponse
      >
        ? RequestGroup<
            TState,
            TOpName,
            TRequestInput,
            TRequest,
            TResponseInput,
            TResponse
          >
        : never

    export namespace relay {
      /**
       * Options for configuring how a {@link RequestGroup.relay} handles
       * concurrent requests.
       *
       * By default, `relay` uses `mergeMap` — all in-flight requests run
       * concurrently and every response reaches the store. This is correct
       * for mutations and writes, where each request is independent.
       *
       * Use `strategy: 'switch'` when only the **latest** request should
       * produce a result — any in-flight request is abandoned as soon as a
       * newer one arrives. This is important for load operations where filters
       * or context can change rapidly: without it, an earlier (stale) response
       * can arrive after a later one and overwrite state with outdated data.
       */
      export interface Options {
        /**
         * Controls how concurrent requests are handled.
         *
         * - `'merge'` — all in-flight requests run concurrently; every
         *   response reaches the store (equivalent to `mergeMap`)
         * - `'switch'` — when a new request arrives, the previous in-flight
         *   request is abandoned; only the latest response reaches the store
         *   (equivalent to `switchMap`)
         *
         * @default 'merge'
         */
        readonly strategy?: 'merge' | 'switch'
      }

      export namespace Target {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        export type Constraint = RequestGroup.Marker
      }

      /**
       * TODO:
       */
      export type Mapping<
        TTo extends Target.Constraint,
        TResponseInput,
      > = AllowMissing.IgnoringAny<{
        /**
         * Input for the inner request
         */
        readonly input: Marker.Input<NoInfer<TTo>, 'request'>
        /**
         * Transforms the response of the inner request(s) into a response
         * for the outer request
         */
        readonly output:
          | ((
              innerResult: Marker.Data<NoInfer<TTo>, 'success'>,
              /**
               * It is possible to map a single outer request into multiple inner
               * requests. In that case, we will dispatch one success once all
               * inner requests are completed. We create this one success by
               * updating after each inner success
               */
              currentOuterResult: TResponseInput | undefined,
            ) => TResponseInput)
          | Extract<TResponseInput, undefined>
        readonly onError?: (
          error: Error,
          source: Marker.Action<TTo, 'error'>,
        ) => void
        readonly onCancellation?: (
          reason: string,
          source: Marker.Action<TTo, 'cancellation'>,
        ) => void
        readonly onSuccess?: (
          innerResult: Marker.Data<NoInfer<TTo>, 'success'>,
          outerResult: TResponseInput,
          source: Marker.Action<TTo, 'success'>,
        ) => void
        /**
         * Executed when a response is submitted, regardless of the
         * outcome
         */
        readonly onResponse?: () => void
      }>

      /**
       * Indicates how to process a request in {@link RequestGroup.relay}
       */
      export type Instruction<
        TTo extends Target.Constraint,
        TResponseInput,
      > = ExactlyOneOf<
        Instruction.Forward<TTo, TResponseInput> &
          Instruction.Sequence<TTo, TResponseInput> &
          UnionToIntersection<Instruction.Terminate<TResponseInput>>
      >
      export namespace Instruction {
        /**
         * Used to immediately emit an outcome (and prevent further
         * forwards of this request)
         */
        export type Terminate<TResponseInput> = {
          /** @see {@link Terminal} */
          readonly [TOpStatus in Op.Status.ForResponse]: Record<
            TOpStatus,
            AllowTrueIfAllOptional<
              Op.Status.Data.Input.WithoutRequest<TOpStatus, TResponseInput>
            >
          >
        }[Op.Status.ForResponse]

        /**
         * Represents an {@link Instruction} to relay a request from one
         * {@link RequestGroup} to another
         */
        export interface Forward<
          TTo extends Target.Constraint,
          TResponseInput,
        > {
          /** @see {@link Forward} */
          // NOTE: NoMissing helps with type extension
          readonly forward: NoMissing<Mapping<TTo, TResponseInput>> & {
            target: TTo
          }
        }

        /**
         * Represents an {@link Instruction} to execute a sequence of
         * {@link Forward}
         *
         * __NOTE:__ stops executing on the first unsuccessful {@link Forward}
         */
        export interface Sequence<
          TTo extends Target.Constraint,
          TResponseInput,
        > {
          /** @see {@link Sequence} */
          readonly sequence: readonly Forward<TTo, TResponseInput>[]
        }
      }
    }
  }
}

type AllowTrueIfAllOptional<T> = If.IsAllOptional<AllowMissing<T>, T | true, T>
